/**
 * CertCreatePage — 船員アプリ内の「証明書の作成・発行」（スマホ最適化）。
 * 船長など admin_documents 権限を持つ乗組員が、船内で各種様式を作成・印刷し、
 * 「陸上へ提出」で陸上管理者へ共有（連絡＋プッシュ通知）できる。
 *  対応様式: 雇入(雇止)届 / 雇入契約変更(更新)届 / 乗組員名簿 / 航行報告書 / 航行報告証明申請書
 *            ＋ 社内様式: 船用品注文書 / 船員旅費精算書（機能フラグ extra_cert_forms が有効な会社のみ）
 *  社内様式は書類権限が無い船員でも作成できる（船内で誰もが書いていた紙の置き換えのため）。
 *  品名・駅・交通内容などは陸上が登録したマスタから選べ、無いものは自由に書ける。
 *  出力: PDF / Excel(雇入系・名簿) / Word(航行報告系) / 端末の印刷サービス
 */
import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AuthUser } from '../hooks/useAuth';
import { apiFetch, apiFetchBlob, apiUpload, getAccessToken } from '../services/api';
import { companionPrint } from '../services/alcohol-companion';
import { hasFeature } from '../utils/featureFlags';
import { isIOS } from '../utils/installPrompt';

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api/v1';

interface Props { user: AuthUser }

const FORMS = [
  { id: 'koyoiri',   label: '雇入届',     full: '雇入（雇止）届出書' },
  { id: 'henko',     label: '変更届',     full: '雇入契約変更（更新）届出書' },
  { id: 'roster',    label: '乗組員名簿', full: '乗組員名簿（List of Crew）' },
  { id: 'koko',      label: '航行報告',   full: '航行報告書' },
  { id: 'koko-cert', label: '報告証明',   full: '航行報告証明申請書' },
  { id: 'shipsupply', label: '船用品注文', full: '船用品注文書' },
  { id: 'travelexp',  label: '旅費精算',   full: '船員旅費精算書' },
  { id: 'koten',      label: '荒天避難',   full: '荒天避難報告書' },
  { id: 'tenmatsu',   label: '顛末書',     full: '顛末書' },
  { id: 'roumu',      label: '労務記録簿', full: '労務管理記録簿（国交省様式）' },
] as const;
type FormId = typeof FORMS[number]['id'];
const isHire = (f: FormId) => f === 'koyoiri' || f === 'henko';
// 様式ごとの機能フラグ。有効にした会社だけタブに出す
const FORM_FLAG: Record<string, string> = {
  shipsupply: 'extra_cert_forms', travelexp: 'extra_cert_forms', koten: 'extra_cert_forms', tenmatsu: 'extra_cert_forms', roumu: 'labor_record_book',
};
// 書類権限が無い船員でも作れる様式（船内で誰もが書いていた紙の置き換え）
const CREW_OPEN_FORMS: string[] = ['shipsupply', 'travelexp', 'koten', 'tenmatsu'];

/** 今日の和暦（「令和 8 年 11 月 2 日」）。日付欄の既定値に使う */
function reiwaToday(): string {
  const d = new Date();
  return `令和 ${d.getFullYear() - 2018} 年 ${d.getMonth() + 1} 月 ${d.getDate()} 日`;
}

/** スマホで撮った写真を data URL にする（長辺 1280px・JPEG 0.72 に縮めて、1枚 200〜400KB 程度に収める） */
function fileToDataUrl(file: File, maxSide = 1280): Promise<string> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
      const c = document.createElement('canvas');
      c.width = Math.round(img.width * scale); c.height = Math.round(img.height * scale);
      c.getContext('2d')!.drawImage(img, 0, 0, c.width, c.height);
      URL.revokeObjectURL(url);
      resolve(c.toDataURL('image/jpeg', 0.72));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('画像を読み込めませんでした')); };
    img.src = url;
  });
}

interface Vessel {
  id: string; vessel_name: string; vessel_no?: string; vessel_type?: string;
  owner_type?: string; owner_name?: string; owner_address?: string;
  gross_tonnage?: string | null; port_of_registry?: string | null; imo_number?: string | null; call_sign?: string | null;
  main_engine_type?: string | null; main_engine_count?: string | null; main_engine_power?: string | null;
}
interface Crew {
  user_id: string; user_name: string; employee_code?: string; birth_date?: string | null; birth_place?: string | null;
  nationality?: string | null; seaman_book_no?: string | null; license_grade?: string | null; position?: string | null;
  vessel_id?: string | null; vessel_name?: string | null;
  /** 選択中の船との関わり（サーバーが乗船中→過去乗船→その他の順で返す） */
  involvement?: 'on_board' | 'past' | 'none';
}
interface Row {
  bookNo: string; name: string; age: string; distinction: string;
  hireDate: string; hirePort: string; endDate: string; endPort: string;
  position: string; period: string; wage: string; allowance: string; otherConditions: string; remarks: string;
  changeDate: string; changeItem: string; newValue: string; oldValue: string;
}
const emptyRow = (): Row => ({ bookNo: '', name: '', age: '', distinction: '', hireDate: '', hirePort: '', endDate: '', endPort: '', position: '', period: '', wage: '', allowance: '', otherConditions: '', remarks: '', changeDate: '', changeItem: '', newValue: '', oldValue: '' });
interface SupplyRow { no: string; itemName: string; size: string; stock: string; qty: string; remarks: string; }
const emptySupply = (): SupplyRow => ({ no: '', itemName: '', size: '', stock: '', qty: '', remarks: '' });
/** 荒天避難報告書の停泊当直（時間帯と甲板部・機関部の当直者） */
interface WatchRow { from: string; to: string; deck: string; engine: string; }
const emptyWatch = (): WatchRow => ({ from: '', to: '', deck: '', engine: '' });
interface TravelRow { month: string; day: string; fromStation: string; toStation: string; transport: string; amount: string; }
const emptyTravel = (): TravelRow => ({ month: '', day: '', fromStation: '', toStation: '', transport: '', amount: '' });
/** 「1,200」「1200円」などの入力から数値を取り出す */
const yenNum = (v: string): number => { const n = Number(String(v ?? '').replace(/[^\d.-]/g, '')); return isFinite(n) ? n : 0; };

interface RosterRow { no: string; name: string; rank: string; nationality: string; birthDate: string; birthPlace: string; seamanBookNo: string; signOnDate: string; }
const emptyRoster = (n: number): RosterRow => ({ no: String(n), name: '', rank: '', nationality: '日本', birthDate: '', birthPlace: '', seamanBookNo: '', signOnDate: '' });

function ageFromBirth(bd?: string | null): string {
  if (!bd) return ''; const d = new Date(bd); if (isNaN(d.getTime())) return '';
  const now = new Date(); let a = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth(); if (m < 0 || (m === 0 && now.getDate() < d.getDate())) a--;
  return a > 0 && a < 120 ? String(a) : '';
}

/** 入力候補（陸上のマスタ由来）。extra は品名に紐づく既定の寸法 */
interface OptionItem { value: string; extra?: string }
type OptionMap = Record<string, OptionItem[]>;
const EMPTY_OPTIONS: OptionMap = { supplierName: [], itemName: [], size: [], remarks: [], loadPort: [], station: [], transport: [], jobTitle: [] };

const inp: React.CSSProperties = { width: '100%', padding: '11px 12px', border: '1px solid #e2e8f0', borderRadius: 10, fontSize: 16, boxSizing: 'border-box', background: '#fff', outline: 'none' };
const lb: React.CSSProperties = { fontSize: 12, fontWeight: 700, color: '#475569', display: 'block', marginBottom: 5, marginTop: 12 };

/**
 * 候補から選べる入力欄。マスタに無いものはそのまま打ち込めるので、
 * 候補が足りなくても現場の作業は止まらない。
 */
const Combo: React.FC<{
  value: string; onChange: (v: string) => void; options?: OptionItem[];
  ph?: string; onPickExtra?: (extra: string) => void;
}> = ({ value, onChange, options = [], ph, onPickExtra }) => {
  const [open, setOpen] = useState(false);
  const q = value.trim().toLowerCase();
  // 入力済みの文字で絞り込む。完全一致しているときは全件出す（選び直せるように）
  const list = !q || options.some(o => o.value === value) ? options : options.filter(o => o.value.toLowerCase().includes(q));
  const pick = (o: OptionItem) => { onChange(o.value); if (o.extra && onPickExtra) onPickExtra(o.extra); setOpen(false); };
  return (
    <div style={{ position: 'relative' }}>
      <input style={{ ...inp, paddingRight: options.length > 0 ? 40 : 12 }} value={value} placeholder={ph}
        onChange={e => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)} />
      {options.length > 0 && (
        <button type="button" tabIndex={-1} aria-label="候補から選ぶ"
          onPointerDown={e => { e.preventDefault(); setOpen(o => !o); }}
          style={{ position: 'absolute', right: 2, top: 2, bottom: 2, width: 36, border: 'none', background: 'transparent', color: '#64748b', fontSize: 12, cursor: 'pointer' }}>▼</button>
      )}
      {open && list.length > 0 && (
        <div style={{ position: 'absolute', zIndex: 60, top: '100%', left: 0, right: 0, marginTop: 4, background: '#fff', border: '1px solid #cbd5e1', borderRadius: 10, boxShadow: '0 10px 28px rgba(15,23,42,0.18)', maxHeight: 244, overflowY: 'auto' }}>
          {list.map(o => (
            <div key={o.value} onPointerDown={e => { e.preventDefault(); pick(o); }}
              style={{ padding: '12px', fontSize: 14.5, borderBottom: '1px solid #f1f5f9', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ flex: 1, minWidth: 0 }}>{o.value}</span>
              {o.extra && <span style={{ fontSize: 12, color: '#94a3b8', flexShrink: 0 }}>{o.extra}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const CertCreatePage: React.FC<Props> = ({ user }) => {
  const nav = useNavigate();
  // 官庁様式は書類権限を持つ人だけ。社内様式は乗組員なら誰でも作れる
  const canOfficialForms = React.useMemo(() => {
    if (['system_admin', 'shore_admin', 'captain'].includes(user.user_role)) return true;
    try { return (JSON.parse(localStorage.getItem('catms_permissions') ?? '{}') as Record<string, boolean>).admin_documents === true; }
    catch { return false; }
  }, [user.user_role]);
  const visibleForms = FORMS.filter(f => {
    const flag = FORM_FLAG[f.id];
    if (flag && !hasFeature(flag)) return false;
    // 社内様式は誰でも、それ以外（官庁様式・労務管理記録簿）は書類権限のある人だけ
    return CREW_OPEN_FORMS.includes(f.id) || canOfficialForms;
  });
  const [form, setForm] = useState<FormId>(() => canOfficialForms ? 'koyoiri' : 'shipsupply');
  const [data, setData] = useState<Record<string, string>>({ shipUse: '貨物船', navArea: '沿海区域', mainEngineType: '内燃', bureauName: '四国運輸局長', department: '甲板部',
    year: String(new Date().getFullYear()), month: String(new Date().getMonth() + 1), holidayDefined: '有' });
  const [rows, setRows] = useState<Row[]>([emptyRow()]);
  const [crewRows, setCrewRows] = useState<RosterRow[]>([emptyRoster(1)]);
  const [opts, setOpts] = useState<OptionMap>(EMPTY_OPTIONS);
  const [supplyRows, setSupplyRows] = useState<SupplyRow[]>([emptySupply()]);
  const [travelRows, setTravelRows] = useState<TravelRow[]>([emptyTravel()]);
  // 荒天避難報告書の停泊当直（紙は8行）／顛末書の現場写真（2ページ目に並ぶ・最大6枚）
  const [watchRows, setWatchRows] = useState<WatchRow[]>(() => Array.from({ length: 4 }, emptyWatch));
  const [photos, setPhotos] = useState<string[]>([]);
  const [vessels, setVessels] = useState<Vessel[]>([]);
  /** 自分が関わってきた船のID（乗船中・過去乗船）。null=未取得 */
  const [myVesselIds, setMyVesselIds] = useState<string[] | null>(null);
  const [crew, setCrew] = useState<Crew[]>([]);
  const [selectedVesselId, setSelectedVesselId] = useState('');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<{ msg: string; type: 'ok' | 'err' } | null>(null);
  const [preview, setPreview] = useState<{ url: string; loading: boolean } | null>(null);
  // 陸上へ提出: 送付先（陸上管理者）を複数選択できる。未選択なら全員へ。
  const [shoreAdmins, setShoreAdmins] = useState<{ id: string; user_name: string }[]>([]);
  const [recipOpen, setRecipOpen] = useState(false);
  // iPhone: 先にPDFを作ってから「印刷・共有」シートを出す（空のタブを先に開いて後から差し替える方式はiPhoneでは白紙になる）
  const [pdfReady, setPdfReady] = useState<{ url: string; file: File | null; title: string } | null>(null);
  const [recipSel, setRecipSel] = useState<string[]>([]);
  const [zoom, setZoom] = useState(1);   // プレビューの拡大率（ボタン＋ピンチで操作。100%=幅フィット）
  const previewScrollRef = useRef<HTMLDivElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const pinchRef = useRef<{ d: number; z: number } | null>(null);
  const focalRef = useRef<{ fracX: number; fracY: number; fx: number; fy: number } | null>(null);
  const zoomRef = useRef(1);
  zoomRef.current = zoom;

  // 焦点（指の中点 or ボタン時は画面中央）を軸に拡大する。次のレイアウトで
  // その点が同じ位置に来るようスクロールを補正するため focalRef に記録しておく。
  const zoomTo = (next: number, focal?: { x: number; y: number }) => {
    const z = Math.min(4, Math.max(1, +next.toFixed(3)));
    const el = previewScrollRef.current, img = imgRef.current;
    if (el && img && img.clientWidth > 0) {
      const rect = el.getBoundingClientRect();
      const fx = focal ? focal.x : rect.left + rect.width / 2;
      const fy = focal ? focal.y : rect.top + rect.height / 2;
      focalRef.current = {
        fracX: (el.scrollLeft + (fx - rect.left)) / img.clientWidth,
        fracY: (el.scrollTop + (fy - rect.top)) / img.clientHeight,
        fx, fy,
      };
    } else { focalRef.current = null; }
    setZoom(z);
  };
  // zoom反映後、焦点が指の下（記録した位置）に来るようスクロール位置を合わせる
  useLayoutEffect(() => {
    const el = previewScrollRef.current, img = imgRef.current, f = focalRef.current;
    if (!el || !img || !f) return;
    const rect = el.getBoundingClientRect();
    el.scrollLeft = f.fracX * img.clientWidth - (f.fx - rect.left);
    el.scrollTop  = f.fracY * img.clientHeight - (f.fy - rect.top);
  }, [zoom]);
  // 対象行に船員を差し込むための一時状態（船員選択シート）
  //   field: data[key] に氏名（titleKey があれば役職も）／ watch: 停泊当直の行×列（甲板部・機関部）
  const [pickerFor, setPickerFor] = useState<
    | { kind: 'hire' | 'roster' | 'person' | 'target'; index: number }
    | { kind: 'field'; key: string; titleKey?: string }
    | { kind: 'watch'; index: number; col: 'deck' | 'engine' }
    | null>(null);

  const set = (k: string, v: string) => setData(d => ({ ...d, [k]: v }));
  const showToast = (msg: string, type: 'ok' | 'err' = 'ok') => { setToast({ msg, type }); setTimeout(() => setToast(null), type === 'err' ? 6000 : 3000); };

  useEffect(() => {
    apiFetch<any>('/users/me/profile').then(r => {
      const c = r?.data ?? r;
      setData(d => ({
        ...d,
        ownerName: d.ownerName || c?.company_name || '',
        ownerAddress: d.ownerAddress || [c?.company_address, c?.company_name].filter(Boolean).join('\n'),
        ownerAddrName: d.ownerAddrName || [c?.company_address, c?.company_name].filter(Boolean).join('\n'),
        masterName: d.masterName || (user.user_role === 'captain' ? user.user_name : d.masterName || ''),
        reportPerson: d.reportPerson || user.user_name,
        // 社内様式の宛先・発行元（船用品注文書の「○○ 御中」、顛末書の「代表取締役 ○○ 様」）
        companyName: d.companyName || c?.company_name || '',
        companyRep: d.companyRep || c?.company_rep || '',
        reporterName: d.reporterName || user.user_name,
        partyName: d.partyName || user.user_name,
      }));
    }).catch(() => {});
    apiFetch<any>('/vessels').then(r => setVessels(r?.data ?? [])).catch(() => {});
    apiFetch<any>('/cert-forms/crew').then(r => {
      setCrew(r?.data ?? []);
      // 自分が関わってきた船（乗船中・過去乗船）。船の候補はこれに絞る
      setMyVesselIds(((r?.my_vessels ?? []) as { id: string }[]).map(v => v.id));
    }).catch(() => {});
    apiFetch<{ data: { id: string; user_name: string }[] }>('/documents/shore-admins').then(r => setShoreAdmins(r?.data ?? [])).catch(() => {});
    // 入力候補（陸上のマスタ）。取れなくても自由記述で書けるので致命的ではない
    apiFetch<{ data: OptionMap }>('/cert-forms/options')
      .then(r => setOpts({ ...EMPTY_OPTIONS, ...(r?.data ?? {}) }))
      .catch(() => {});
  }, [user.user_role, user.user_name]);

  // 船を選んだら、その船を基準にした優先順（乗船中→過去乗船→その他）で船員候補を取り直す
  useEffect(() => {
    if (!selectedVesselId) return;
    apiFetch<any>(`/cert-forms/crew?vessel_id=${encodeURIComponent(selectedVesselId)}`)
      .then(r => setCrew(r?.data ?? []))
      .catch(() => {});
  }, [selectedVesselId]);

  // ── 入力の手間を減らす自動入力 ──
  // ① 船は選ばなくても決まる: 自分が乗船中の船（無ければ候補が1隻ならそれ）を最初から選んでおく
  const autoVesselDone = React.useRef(false);
  useEffect(() => {
    if (autoVesselDone.current || selectedVesselId || vessels.length === 0 || myVesselIds === null) return;
    const mine = crew.find(c => c.user_id === user.user_id)?.vessel_id;
    const pick = vessels.find(v => v.id === mine)
      ?? (myVesselIds.length === 1 ? vessels.find(v => v.id === myVesselIds[0]) : undefined)
      ?? (vessels.length === 1 ? vessels[0] : undefined);
    if (pick) { autoVesselDone.current = true; applyVessel(pick); }
  }, [vessels, myVesselIds, crew, selectedVesselId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ② 船が決まれば人も決まる: 乗船中の船長・機関長を船長名・機関長名・承認欄に、自分の役職を作成者・当事者の役職に入れる
  useEffect(() => {
    if (!selectedVesselId) return;
    const onBoard = crew.filter(c => c.vessel_id === selectedVesselId && c.involvement !== 'none');
    const byPos = (kw: string) => onBoard.find(c => (c.position ?? '').includes(kw))?.user_name ?? '';
    const captain = byPos('船長'), chief = byPos('機関長');
    const me = crew.find(c => c.user_id === user.user_id);
    const myPos = me?.position || me?.license_grade || '';
    setData(d => ({
      ...d,
      ...(captain ? { masterName: captain, approverMaster: d.approverMaster || captain.split(/[\s　]/)[0] } : {}),
      ...(chief ? { chiefEngName: chief } : {}),
      ...(myPos ? { reporterTitle: d.reporterTitle || myPos, partyTitle: d.partyTitle || myPos, reportJobTitle: d.reportJobTitle || myPos } : {}),
    }));
  }, [crew, selectedVesselId, user.user_id]);

  // ③ 日付は今日を入れておく（変えたいときだけ直す）
  useEffect(() => {
    const today = reiwaToday();
    setData(d => ({
      ...d,
      ...(form === 'shipsupply' && !d.orderDate ? { orderDate: today } : {}),
      ...((form === 'koten' || form === 'tenmatsu' || form === 'koko') && !d.reportDate ? { reportDate: today } : {}),
      ...(form === 'koko-cert' && !d.applicationDate ? { applicationDate: today } : {}),
    }));
  }, [form]);

  const applyVessel = (v?: Vessel) => {
    if (!v) return;
    setSelectedVesselId(v.id);
    const engineCombined = [v.main_engine_type, v.main_engine_count ? `${v.main_engine_count}基` : '', v.main_engine_power ? `${v.main_engine_power}kW` : ''].filter(Boolean).join(' ');
    const hireEngineType = (v.main_engine_type ?? '').includes('汽') ? '汽' : (v.main_engine_type ? '内燃' : '');
    setData(d => ({
      ...d,
      shipName: v.vessel_name ?? '',
      ...(v.vessel_no ? { shipNo: v.vessel_no } : {}),
      ...(v.vessel_type ? { shipUse: v.vessel_type } : {}),
      ...(v.gross_tonnage ? { grossTonnage: String(v.gross_tonnage) } : {}),
      ...(v.port_of_registry ? { portOfRegistry: String(v.port_of_registry) } : {}),
      ...(v.imo_number ? { imoNumber: String(v.imo_number) } : {}),
      ...(v.call_sign ? { callSign: String(v.call_sign) } : {}),
      ...(engineCombined ? { mainEngine: engineCombined } : {}),
      ...(hireEngineType ? { mainEngineType: hireEngineType } : {}),
      ...(v.main_engine_power ? { mainEnginePower: String(v.main_engine_power) } : {}),
      ...(v.owner_type && v.owner_type !== 'self'
        ? { ownerName: v.owner_name ?? d.ownerName, ownerAddress: [v.owner_address, v.owner_name].filter(Boolean).join('\n'), ownerAddrName: [v.owner_address, v.owner_name].filter(Boolean).join('\n') }
        : {}),
    }));
  };

  // 乗組員名簿: 選択船の乗組員（乗船中）を一括取込
  const importRoster = () => {
    if (!selectedVesselId) { showToast('先に船舶を選択してください', 'err'); return; }
    const list = crew.filter(c => c.vessel_id === selectedVesselId);
    if (list.length === 0) { showToast('この船の乗組員が見つかりません', 'err'); return; }
    setCrewRows(list.map((c, i) => ({
      no: String(i + 1), name: c.user_name, rank: c.position || c.license_grade || '', nationality: c.nationality || '日本',
      birthDate: c.birth_date ? c.birth_date.slice(0, 10) : '', birthPlace: c.birth_place || '', seamanBookNo: c.seaman_book_no || '', signOnDate: '',
    })));
    showToast(`${list.length}名を取り込みました`);
  };

  const setRow = (i: number, patch: Partial<Row>) => setRows(rs => rs.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const setRosterRow = (i: number, patch: Partial<RosterRow>) => setCrewRows(rs => rs.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const setSupplyRow = (i: number, patch: Partial<SupplyRow>) => setSupplyRows(rs => rs.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const setTravelRow = (i: number, patch: Partial<TravelRow>) => setTravelRows(rs => rs.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const travelTotal = travelRows.reduce((a, r) => a + yenNum(r.amount), 0);

  const applyCrewToRow = (c: Crew) => {
    if (!pickerFor) return;
    if (pickerFor.kind === 'field') {
      const { key, titleKey } = pickerFor;
      setData(d => ({ ...d, [key]: c.user_name, ...(titleKey ? { [titleKey]: c.position || c.license_grade || d[titleKey] || '' } : {}) }));
      setPickerFor(null);
      return;
    }
    if (pickerFor.kind === 'watch') {
      setWatchRow(pickerFor.index, { [pickerFor.col]: c.user_name });
      setPickerFor(null);
      return;
    }
    if (pickerFor.kind === 'hire') {
      setRow(pickerFor.index, { name: c.user_name, bookNo: c.seaman_book_no ?? '', age: ageFromBirth(c.birth_date) || '', position: c.position || c.license_grade || '' });
    } else if (pickerFor.kind === 'person') {
      setData(d => ({ ...d, reportPerson: c.user_name, reportJobTitle: c.position || c.license_grade || d.reportJobTitle || '' }));
    } else if (pickerFor.kind === 'target') {
      setData(d => ({ ...d, userId: c.user_id, userName: c.user_name }));
    } else {
      setRosterRow(pickerFor.index, { name: c.user_name, rank: c.position || c.license_grade || '', nationality: c.nationality || '日本', birthDate: c.birth_date ? c.birth_date.slice(0, 10) : '', birthPlace: c.birth_place || '', seamanBookNo: c.seaman_book_no || '' });
    }
    setPickerFor(null);
  };

  const buildData = useCallback(() => {
    const base: Record<string, unknown> = { ...data };
    if (isHire(form)) base.rows = rows.map(r => ({ ...r, distinction: r.distinction || (form === 'koyoiri' ? '雇入' : '変更') }));
    if (form === 'roster') base.crewRows = crewRows.filter(r => r.name.trim());
    if (form === 'shipsupply') base.supplyRows = supplyRows.filter(r => r.itemName.trim() || r.remarks.trim() || r.qty.trim());
    if (form === 'travelexp') base.travelRows = travelRows.filter(r => r.fromStation.trim() || r.toStation.trim() || r.transport.trim() || r.amount.trim());
    if (form === 'koten') base.watchRows = watchRows.filter(r => r.from.trim() || r.to.trim() || r.deck.trim() || r.engine.trim());
    if (form === 'tenmatsu') base.photos = photos;
    return base;
  }, [data, rows, crewRows, supplyRows, travelRows, watchRows, photos, form]);
  const setWatchRow = (i: number, patch: Partial<WatchRow>) => setWatchRows(rs => rs.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const addPhoto = async (file?: File | null) => {
    if (!file) return;
    if (photos.length >= 6) { showToast('写真は6枚までです', 'err'); return; }
    try { const u = await fileToDataUrl(file); setPhotos(p => [...p, u]); } catch { showToast('写真を読み込めませんでした', 'err'); }
  };

  const fetchBlob = async (fmt: string) => apiFetchBlob(`/cert-forms/${form}?format=${fmt}`, { method: 'POST', body: JSON.stringify({ data: buildData() }) });

  const openPreview = async () => {
    focalRef.current = null;
    setZoom(1);
    setPreview({ url: '', loading: true });
    try {
      const blob = await fetchBlob('png');
      setPreview({ url: URL.createObjectURL(blob), loading: false });
    } catch { setPreview(null); showToast('プレビューの生成に失敗しました', 'err'); }
  };

  // プレビューの2本指ピンチズーム（viewport/WebViewのネイティブ設定に依存せず動く独自実装。
  // 指の中点を焦点に拡大するので、拡大した箇所がそのまま指の下に留まる）
  useEffect(() => {
    const el = previewScrollRef.current;
    if (!el || !preview) return;
    const dist = (t: TouchList) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
    const mid = (t: TouchList) => ({ x: (t[0].clientX + t[1].clientX) / 2, y: (t[0].clientY + t[1].clientY) / 2 });
    const onStart = (e: TouchEvent) => { if (e.touches.length === 2) pinchRef.current = { d: dist(e.touches), z: zoomRef.current }; };
    const onMove = (e: TouchEvent) => {
      if (e.touches.length === 2 && pinchRef.current) {
        e.preventDefault();  // ネイティブのスクロール/ズームを止めて自前で拡大
        zoomTo(pinchRef.current.z * (dist(e.touches) / pinchRef.current.d), mid(e.touches));
      }
    };
    const onEnd = (e: TouchEvent) => { if (e.touches.length < 2) pinchRef.current = null; };
    el.addEventListener('touchstart', onStart, { passive: false });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd);
    el.addEventListener('touchcancel', onEnd);
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview]);

  const isNativeApp = !!(window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor?.isNativePlatform?.();

  // PDFの表示/ダウンロード/印刷。
  //  - 管理端末APK（ブラウザ非搭載のキオスクが多い）: 外部でPDFを開けないため
  //    アプリ内プレビューで全内容を確認できるようにする（画像は表示可能）。
  //  - ブラウザ/PWA: PDFをサーバーに一時保管→GETのURLを window.open で開く。
  const openCert = async (dl: boolean) => {
    if (isNativeApp) {
      // 印刷（dl=false）: Companion経由で端末の印刷機能→船内ネットワークプリンターへ送る。
      if (!dl) {
        setBusy(true);
        try {
          const res = await apiFetch<{ success: boolean; data: { token: string } }>(`/cert-forms/${form}/prepare`, { method: 'POST', body: JSON.stringify({ data: buildData() }) });
          const stash = res?.data?.token;
          if (stash) {
            const abs = API_BASE.startsWith('http') ? API_BASE : window.location.origin + API_BASE;
            const url = `${abs}/cert-forms/stash/${stash}?token=${encodeURIComponent(getAccessToken() ?? '')}`;
            const ok = await companionPrint(url, FORMS.find(f => f.id === form)!.full);
            setBusy(false);
            if (ok) { showToast('プリンターへ送信しました。印刷画面でプリンターを選び「印刷」を押してください。'); return; }
          } else { setBusy(false); }
        } catch { setBusy(false); }
        // Companion未導入/失敗 → プレビュー表示＋案内
        openPreview();
        showToast('この端末で直接印刷できませんでした。プレビューで確認し「🏢 陸上へ提出」または管理サイト(PC)から印刷してください。', 'err');
        return;
      }
      // PDF（保存）: キオスクにはビューア/ファイル管理が無いためプレビューで内容を表示
      openPreview();
      showToast('この端末ではプレビューで内容を確認できます。保存は「🏢 陸上へ提出」または管理サイト(PC)から行ってください。', 'err');
      return;
    }
    // iPhone/iPad: 空のタブを先に開いてPDFのURLへ差し替える方式は、ホーム画面に追加したアプリでは
    // 差し替えが効かず白紙のままになる（Safariでも不安定）。先にPDFを作り、シートの操作で
    // 共有シート（プリント・ファイルに保存）か、PDFのURLを直接開く。
    if (isIOS()) {
      setBusy(true);
      try {
        const res = await apiFetch<{ success: boolean; data: { token: string } }>(`/cert-forms/${form}/prepare`, { method: 'POST', body: JSON.stringify({ data: buildData() }) });
        const stash = res?.data?.token;
        if (!stash) throw new Error('生成に失敗しました');
        const abs = API_BASE.startsWith('http') ? API_BASE : window.location.origin + API_BASE;
        const url = `${abs}/cert-forms/stash/${stash}?token=${encodeURIComponent(getAccessToken() ?? '')}`;
        const title = FORMS.find(f => f.id === form)!.full + (data.shipName ? `（${data.shipName}）` : '');
        let file: File | null = null;
        try {
          const blob = await apiFetchBlob(`/cert-forms/stash/${stash}`);
          file = new File([blob], `${title}.pdf`, { type: 'application/pdf' });
        } catch { /* 共有用の取得に失敗しても「PDFを開く」は使える */ }
        setPdfReady({ url, file, title });
      } catch (e: any) {
        showToast(e?.message ?? '出力に失敗しました', 'err');
      }
      setBusy(false);
      return;
    }
    const w = window.open('', '_blank');  // ポップアップブロック回避のため操作中に確保
    setBusy(true);
    try {
      const res = await apiFetch<{ success: boolean; data: { token: string } }>(`/cert-forms/${form}/prepare`, { method: 'POST', body: JSON.stringify({ data: buildData() }) });
      const stash = res?.data?.token;
      if (!stash) throw new Error('生成に失敗しました');
      const url = `${API_BASE}/cert-forms/stash/${stash}?token=${encodeURIComponent(getAccessToken() ?? '')}${dl ? '&dl=1' : ''}`;
      if (w && !w.closed) w.location.href = url;
      else window.open(url, '_blank');
    } catch (e: any) {
      if (w && !w.closed) w.close();
      showToast(e?.message ?? '出力に失敗しました', 'err');
    }
    setBusy(false);
  };

  // 陸上へ提出: まず送付先（陸上管理者）を選ぶシートを開く
  const submitToOffice = () => { setRecipSel([]); setRecipOpen(true); };
  // 実提出: PDFを生成し、選択した陸上管理者（未選択なら全員）へ提出（連絡＋通知が届く）
  const doSubmitToOffice = async () => {
    setRecipOpen(false);
    setBusy(true);
    try {
      const blob = await fetchBlob('pdf');
      const title = FORMS.find(f => f.id === form)!.full + (data.shipName ? `（${data.shipName}）` : '');
      const fd = new FormData();
      fd.append('file', new File([blob], `${title}.pdf`, { type: 'application/pdf' }));
      fd.append('title', title);
      fd.append('form', form);   // 社内様式は書類権限が無い船員でも提出できる（サーバー側で判定）
      recipSel.forEach(id => fd.append('recipient_user_ids', id));
      const res = await apiUpload<{ data: { notified: number } }>('/documents/submit', fd);
      showToast(`陸上へ提出しました（管理者${res?.data?.notified ?? 0}名へ通知）`);
    } catch (e: any) { showToast(e?.message ?? '提出に失敗しました', 'err'); }
    setBusy(false);
  };

  const F = (label: string, key: string, opts: { textarea?: boolean; ph?: string; type?: string } = {}) => (
    <div><label style={lb}>{label}</label>
      {opts.textarea
        ? <textarea style={{ ...inp, resize: 'vertical', minHeight: 64 }} value={data[key] ?? ''} onChange={e => set(key, e.target.value)} placeholder={opts.ph} />
        : <input style={inp} type={opts.type ?? 'text'} value={data[key] ?? ''} onChange={e => set(key, e.target.value)} placeholder={opts.ph} />}
    </div>
  );

  /** 氏名の入力欄＋「👤」ボタン（乗組員から選ぶ）。titleKey を渡すと役職も一緒に入る */
  const NameField = (label: string, key: string, titleKey?: string, ph?: string) => (
    <div><label style={lb}>{label}</label>
      <div style={{ display: 'flex', gap: 6 }}>
        <input style={{ ...inp, flex: 1, minWidth: 0 }} value={data[key] ?? ''} onChange={e => set(key, e.target.value)} placeholder={ph} />
        <button type="button" onClick={() => setPickerFor({ kind: 'field', key, titleKey })} aria-label="乗組員から選ぶ"
          style={{ width: 48, flexShrink: 0, borderRadius: 10, border: '1px solid #bae6fd', background: '#f0f9ff', color: '#0369a1', fontSize: 18, cursor: 'pointer' }}>👤</button>
      </div>
    </div>
  );
  /** 乗組員の名前チップ（タップで入る）。候補が多いときは乗船中だけ */
  const NameChips = (onPick: (c: Crew) => void, exclude: string[] = []) => {
    const list = crew.filter(c => (selectedVesselId ? c.vessel_id === selectedVesselId : true) && c.involvement !== 'none' && !exclude.includes(c.user_name)).slice(0, 12);
    if (list.length === 0) return null;
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
        {list.map(c => (
          <button key={c.user_id} type="button" onClick={() => onPick(c)}
            style={{ fontSize: 12.5, fontWeight: 700, color: '#0f172a', background: '#fff', border: '1px solid #cbd5e1', borderRadius: 999, padding: '6px 11px', cursor: 'pointer' }}>
            {c.user_name}{c.position ? <span style={{ color: '#94a3b8', fontWeight: 400 }}>・{c.position}</span> : null}
          </button>
        ))}
      </div>
    );
  };

  const currentFull = FORMS.find(f => f.id === form)!.full;
  // 船員候補は絞り込まず、サーバーの優先順（選択中の船の乗船中→過去乗船→その他）のまま出す
  const crewForVessel = crew;
  // 船の候補: 船員・船長は自分が関わってきた船（乗船中・過去乗船）だけ。管理者は全船
  const adminish = ['system_admin', 'shore_admin', 'admin'].includes(user.user_role);
  const vesselChoices = (!adminish && myVesselIds && myVesselIds.length > 0)
    ? vessels.filter(v => myVesselIds.includes(v.id))
    : vessels;

  return (
    <div className="app-scroll-bottom-safe" style={{ flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', background: '#f1f5f9' }}>
      {/* ヘッダ（このスクロールコンテナ内で固定） */}
      <div style={{ position: 'sticky', top: 0, zIndex: 20, background: '#0f172a', color: '#fff' }}>
        <div className="app-header" style={{ maxWidth: 640, margin: '0 auto', padding: '0 14px 10px', display: 'flex', alignItems: 'center', gap: 10, border: 'none', boxShadow: 'none' }}>
          <button onClick={() => nav('/home')} style={{ background: 'rgba(255,255,255,0.14)', border: 'none', color: '#fff', width: 34, height: 34, borderRadius: 9, fontSize: 18, cursor: 'pointer', flexShrink: 0 }}>‹</button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 800 }}>証明書の作成・発行</div>
            <div style={{ fontSize: 11, color: '#93c5fd', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{currentFull}</div>
          </div>
        </div>
      </div>

      {/* 中央寄せの縦1カラム（PCでも崩れない・ページ全体が普通にスクロール） */}
      <div style={{ maxWidth: 640, margin: '0 auto', padding: '12px 14px 48px' }}>
        {visibleForms.length === 0 && (
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20, fontSize: 14, color: '#475569', lineHeight: 1.9 }}>
            作成できる書類がありません。<br />
            書類の作成権限が必要です。船長または陸上の管理者にご相談ください。
          </div>
        )}

        {/* 様式タブ（折返し表示・横スクロール不要） */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
          {visibleForms.map(f => (
            <button key={f.id} onClick={() => { setForm(f.id); setPreview(null); }}
              style={{ padding: '8px 15px', borderRadius: 999, border: form === f.id ? '2px solid #4338ca' : '1px solid #cbd5e1', background: form === f.id ? '#eef2ff' : '#fff', color: form === f.id ? '#3730a3' : '#475569', fontSize: 13.5, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              {f.label}
            </button>
          ))}
        </div>

        <div style={{ display: visibleForms.length === 0 ? 'none' : undefined }}>
        {/* 船舶選択（共通。労務管理記録簿は対象者と年月だけで作れるので出さない） */}
        <div style={{ display: form === 'roumu' ? 'none' : undefined }}>
        <label style={lb}>{CREW_OPEN_FORMS.includes(form) ? '船（船名・船長名・機関長名を自動入力）' : '船舶を選択（船名・番号・所有者などを自動入力）'}</label>
        {vesselChoices.length > 0 && vesselChoices.length <= 6
          ? <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {vesselChoices.map(v => {
                const on = selectedVesselId === v.id;
                return (
                  <button key={v.id} type="button" onClick={() => applyVessel(v)}
                    style={{ padding: '9px 14px', borderRadius: 10, border: on ? '2px solid #0057d9' : '1px solid #cbd5e1', background: on ? '#eff6ff' : '#fff', color: on ? '#0057d9' : '#334155', fontSize: 14, fontWeight: 800, cursor: 'pointer' }}>
                    🚢 {v.vessel_name}
                  </button>
                );
              })}
            </div>
          : <select style={inp} value={selectedVesselId} onChange={e => applyVessel(vessels.find(v => v.id === e.target.value))}>
              <option value="">-- 船舶を選択 --</option>
              {vesselChoices.map(v => <option key={v.id} value={v.id}>{v.vessel_name}</option>)}
            </select>}
        </div>

        {/* ── 雇入・変更届 ── */}
        {isHire(form) && (<>
          {F('届出年月日', 'applicationDate', { ph: '令和 6 年 7 月 19 日' })}
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>{F('船舶番号', 'shipNo')}</div>
            <div style={{ flex: 1 }}>{F('船名', 'shipName')}</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>{F('総トン数', 'grossTonnage')}</div>
            <div style={{ flex: 1 }}>{F('航行区域', 'navArea')}</div>
          </div>
          {F('届出者氏名（肩書つき・例: 船舶所有者 株式会社◯◯／船長 ◯◯）', 'reporterName')}
          {F('船舶所有者の氏名又は名称', 'ownerName')}
          {F('船舶所有者の住所及び氏名又は名称', 'ownerAddress', { textarea: true })}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 18, marginBottom: 4 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>対象船員（{rows.length}名）</div>
            <button onClick={() => setRows(rs => [...rs, emptyRow()])} style={{ fontSize: 13, fontWeight: 800, color: '#2563eb', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '6px 12px', cursor: 'pointer' }}>＋ 追加</button>
          </div>
          {rows.map((r, i) => (
            <div key={i} style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: 12, marginBottom: 10, background: '#fff' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 800, color: '#64748b' }}>#{i + 1}</span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => setPickerFor({ kind: 'hire', index: i })} style={{ fontSize: 12, fontWeight: 800, color: '#0369a1', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: '5px 10px', cursor: 'pointer' }}>👤 船員から選択</button>
                  {rows.length > 1 && <button onClick={() => setRows(rs => rs.filter((_, idx) => idx !== i))} style={{ fontSize: 12, color: '#ef4444', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '5px 10px', cursor: 'pointer' }}>削除</button>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ flex: 2 }}><label style={lb}>氏名</label><input style={inp} value={r.name} onChange={e => setRow(i, { name: e.target.value })} /></div>
                <div style={{ width: 74 }}><label style={lb}>年齢</label><input style={inp} inputMode="numeric" value={r.age} onChange={e => setRow(i, { age: e.target.value })} /></div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ flex: 1 }}><label style={lb}>船員手帳番号</label><input style={inp} value={r.bookNo} onChange={e => setRow(i, { bookNo: e.target.value })} /></div>
                <div style={{ width: 96 }}><label style={lb}>区別</label>
                  <select style={inp} value={r.distinction || (form === 'koyoiri' ? '雇入' : '変更')} onChange={e => setRow(i, { distinction: e.target.value })}>
                    {(form === 'koyoiri' ? ['雇入', '雇止'] : ['変更', '更新']).map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ flex: 1 }}><label style={lb}>雇入年月日</label><input style={inp} value={r.hireDate} onChange={e => setRow(i, { hireDate: e.target.value })} /></div>
                <div style={{ flex: 1 }}><label style={lb}>雇入港</label><input style={inp} value={r.hirePort} onChange={e => setRow(i, { hirePort: e.target.value })} /></div>
              </div>
              <div><label style={lb}>職務</label><input style={inp} value={r.position} onChange={e => setRow(i, { position: e.target.value })} /></div>
              {form === 'koyoiri' ? (<>
                <div style={{ display: 'flex', gap: 8 }}>
                  <div style={{ flex: 1 }}><label style={lb}>雇止年月日</label><input style={inp} value={r.endDate} onChange={e => setRow(i, { endDate: e.target.value })} /></div>
                  <div style={{ flex: 1 }}><label style={lb}>雇止港</label><input style={inp} value={r.endPort} onChange={e => setRow(i, { endPort: e.target.value })} /></div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <div style={{ flex: 1 }}><label style={lb}>給料</label><input style={inp} value={r.wage} onChange={e => setRow(i, { wage: e.target.value })} /></div>
                  <div style={{ flex: 1 }}><label style={lb}>手当</label><input style={inp} value={r.allowance} onChange={e => setRow(i, { allowance: e.target.value })} /></div>
                </div>
              </>) : (<>
                <div style={{ display: 'flex', gap: 8 }}>
                  <div style={{ flex: 1 }}><label style={lb}>変更(更新)年月日</label><input style={inp} value={r.changeDate} onChange={e => setRow(i, { changeDate: e.target.value })} /></div>
                  <div style={{ flex: 1 }}><label style={lb}>変更事項</label><input style={inp} value={r.changeItem} onChange={e => setRow(i, { changeItem: e.target.value })} /></div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <div style={{ flex: 1 }}><label style={lb}>新</label><input style={inp} value={r.newValue} onChange={e => setRow(i, { newValue: e.target.value })} /></div>
                  <div style={{ flex: 1 }}><label style={lb}>旧</label><input style={inp} value={r.oldValue} onChange={e => setRow(i, { oldValue: e.target.value })} /></div>
                </div>
              </>)}
            </div>
          ))}
        </>)}

        {/* ── 乗組員名簿 ── */}
        {form === 'roster' && (<>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>{F('総トン数', 'grossTonnage')}</div>
            <div style={{ flex: 1 }}>{F('IMO番号', 'imoNumber')}</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>{F('船籍港', 'portOfRegistry')}</div>
            <div style={{ flex: 1 }}>{F('作成年月日', 'issueDate', { ph: '令和 6 年 7 月 19 日' })}</div>
          </div>
          {F('船長氏名', 'masterName')}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 18, marginBottom: 4 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>乗組員（{crewRows.length}名）</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={importRoster} style={{ fontSize: 13, fontWeight: 800, color: '#059669', background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 8, padding: '6px 12px', cursor: 'pointer' }}>⤓ 乗組員を取込</button>
              <button onClick={() => setCrewRows(rs => [...rs, emptyRoster(rs.length + 1)])} style={{ fontSize: 13, fontWeight: 800, color: '#2563eb', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '6px 12px', cursor: 'pointer' }}>＋</button>
            </div>
          </div>
          {crewRows.map((r, i) => (
            <div key={i} style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: 12, marginBottom: 10, background: '#fff' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 800, color: '#64748b' }}>#{i + 1}</span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => setPickerFor({ kind: 'roster', index: i })} style={{ fontSize: 12, fontWeight: 800, color: '#0369a1', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: '5px 10px', cursor: 'pointer' }}>👤 選択</button>
                  {crewRows.length > 1 && <button onClick={() => setCrewRows(rs => rs.filter((_, idx) => idx !== i))} style={{ fontSize: 12, color: '#ef4444', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '5px 10px', cursor: 'pointer' }}>削除</button>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ flex: 2 }}><label style={lb}>氏名</label><input style={inp} value={r.name} onChange={e => setRosterRow(i, { name: e.target.value })} /></div>
                <div style={{ flex: 1 }}><label style={lb}>職名</label><input style={inp} value={r.rank} onChange={e => setRosterRow(i, { rank: e.target.value })} /></div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ width: 90 }}><label style={lb}>国籍</label><input style={inp} value={r.nationality} onChange={e => setRosterRow(i, { nationality: e.target.value })} /></div>
                <div style={{ flex: 1 }}><label style={lb}>生年月日</label><input style={inp} value={r.birthDate} onChange={e => setRosterRow(i, { birthDate: e.target.value })} placeholder="1980-01-01" /></div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ flex: 1 }}><label style={lb}>船員手帳番号</label><input style={inp} value={r.seamanBookNo} onChange={e => setRosterRow(i, { seamanBookNo: e.target.value })} /></div>
                <div style={{ flex: 1 }}><label style={lb}>乗船年月日</label><input style={inp} value={r.signOnDate} onChange={e => setRosterRow(i, { signOnDate: e.target.value })} /></div>
              </div>
            </div>
          ))}
        </>)}

        {/* ── 航行報告書 ── */}
        {form === 'koko' && (<>
          {F('報告年月日', 'reportDate', { ph: '令和 6 年 7 月 19 日' })}
          {F('提出先（運輸局長）', 'bureauName', { ph: '四国運輸局長' })}
          {F('件名', 'subject', { ph: '例: 衝突 / 火災 / 遭難船舶救助' })}
          {F('船長氏名', 'masterName')}
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>{F('船名', 'shipName')}</div>
            <div style={{ width: 92 }}>{F('船質', 'hullMaterial', { ph: '鋼' })}</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>{F('船舶番号', 'shipNo')}</div>
            <div style={{ flex: 1 }}>{F('船籍港', 'portOfRegistry')}</div>
          </div>
          {F('総トン数', 'grossTonnage')}
          {F('主機の種類、箇数及び出力', 'mainEngine', { ph: '例: ディーゼル 1基 1235kw' })}
          {F('船舶所有者の住所及び氏名又は名称', 'ownerAddrName', { textarea: true })}
          {F('船長の住所及び氏名', 'masterAddrName', { textarea: true })}
          {F('機関長の住所及び氏名', 'chiefEngAddrName', { textarea: true })}
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>{F('発航港', 'departurePort')}</div>
            <div style={{ flex: 1 }}>{F('到達港', 'arrivalPort')}</div>
          </div>
          {F('事実発生の年月日時及び場所', 'eventDateTimePlace', { textarea: true })}
          {F('事実のてん末', 'eventDetail', { textarea: true })}
        </>)}

        {/* ── 航行報告証明申請書 ── */}
        {form === 'koko-cert' && (<>
          {F('申請年月日', 'applicationDate', { ph: '令和 6 年 7 月 19 日' })}
          {F('提出先（運輸局長）', 'bureauName', { ph: '四国運輸局長' })}
          {F('申請者氏名（「船長」等の肩書を付す）', 'applicantName', { ph: '例: 船長 山田太郎' })}
          {F('住所', 'applicantAddress', { textarea: true })}
          {F('報告先', 'reportAddressee')}
          {F('報告年月日', 'reportDate', { ph: '令和 6 年 7 月 18 日' })}
          {F('証明書の通数', 'certCount', { type: 'text' })}
          {F('証明書の用途', 'certPurpose', { textarea: true })}
        </>)}

        {/* ── 船用品注文書（社内様式・2026.10.30 原紙） ── */}
        {form === 'shipsupply' && (<>
          <label style={lb}>宛先（○○ 御中）　※空欄なら自社名</label>
          <Combo value={data.supplierName ?? ''} onChange={v => set('supplierName', v)} options={opts.supplierName} ph={data.companyName || '例: 株式会社◯◯'} />
          {F('注文年月日', 'orderDate', { ph: '令和 8 年 11 月 2 日' })}
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>{F('船名', 'shipName')}</div>
            <div style={{ width: 120 }}>
              <label style={lb}>部署</label>
              <select style={inp} value={data.department ?? '甲板部'} onChange={e => set('department', e.target.value)}>
                {['甲板部', '機関部'].map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <label style={lb}>積込希望地</label>
              <Combo value={data.loadPort ?? ''} onChange={v => set('loadPort', v)} options={opts.loadPort} ph="今治" />
            </div>
            <div style={{ flex: 1 }}>{F('積込希望日', 'loadDate', { ph: '11 月 20 日' })}</div>
          </div>
          <label style={lb}>承認欄の氏名（船長／責任者／担当者）　※船長は船から自動入力</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input style={inp} value={data.approverMaster ?? ''} placeholder="船長" onChange={e => set('approverMaster', e.target.value)} />
            <input style={inp} value={data.approverResp ?? ''} placeholder="責任者" onChange={e => set('approverResp', e.target.value)} />
            <input style={inp} value={data.approverStaff ?? ''} placeholder="担当者" onChange={e => set('approverStaff', e.target.value)} />
          </div>
          {NameChips(c => setData(d => ({ ...d, [!d.approverResp ? 'approverResp' : 'approverStaff']: c.user_name.split(/[\s　]/)[0] })), [data.approverMaster ?? '', data.approverResp ?? '', data.approverStaff ?? ''])}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 18, marginBottom: 4 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>注文品（{supplyRows.length}件）</div>
            <button onClick={() => setSupplyRows(rs => [...rs, emptySupply()])} style={{ fontSize: 13, fontWeight: 800, color: '#2563eb', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '6px 12px', cursor: 'pointer' }}>＋ 追加</button>
          </div>
          {supplyRows.map((r, i) => (
            <div key={i} style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: 12, marginBottom: 10, background: '#fff' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 800, color: '#64748b' }}>#{i + 1}</span>
                {supplyRows.length > 1 && <button onClick={() => setSupplyRows(rs => rs.filter((_, idx) => idx !== i))} style={{ fontSize: 12, color: '#ef4444', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '5px 10px', cursor: 'pointer' }}>削除</button>}
              </div>
              <label style={lb}>品名</label>
              {/* 品名を候補から選ぶと、マスタに登録された既定の寸法も一緒に入る */}
              <Combo value={r.itemName} onChange={v => setSupplyRow(i, { itemName: v })} options={opts.itemName} ph="例: 潤滑油 SAE40"
                onPickExtra={v => setSupplyRow(i, { size: v })} />
              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <label style={lb}>型式及び寸法</label>
                  <Combo value={r.size} onChange={v => setSupplyRow(i, { size: v })} options={opts.size} ph="20L缶" />
                </div>
                <div style={{ width: 88 }}><label style={lb}>在庫数</label><input style={inp} inputMode="numeric" value={r.stock} onChange={e => setSupplyRow(i, { stock: e.target.value })} /></div>
                <div style={{ width: 88 }}><label style={lb}>注文数</label><input style={inp} inputMode="numeric" value={r.qty} onChange={e => setSupplyRow(i, { qty: e.target.value })} /></div>
              </div>
              <label style={lb}>備考欄</label>
              <Combo value={r.remarks} onChange={v => setSupplyRow(i, { remarks: v })} options={opts.remarks} />
            </div>
          ))}
          <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.6, marginTop: 6 }}>※ 現場の状況が分かるよう、可能な範囲で写真・動画（寄り・引き）を撮って、連絡で陸上に共有してください。</div>
        </>)}

        {/* ── 荒天避難報告書（社内様式・2026.10.30 原紙） ── */}
        {form === 'koten' && (<>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>{F('報告年月日', 'reportDate', { ph: '令和 8 年 11 月 2 日' })}</div>
            <div style={{ flex: 1 }}>{F('船名', 'shipName')}</div>
          </div>
          {F('避難日時（から）', 'evacFrom', { ph: '令和 8 年 11 月 1 日（土） 14 時 30 分' })}
          {F('避難日時（まで）', 'evacTo', { ph: '令和 8 年 11 月 2 日（日） 6 時 00 分' })}
          {F('避難場所', 'evacPlace', { ph: '例: 来島海峡 ○○沖' })}
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>{F('緯度', 'latitude', { ph: "34°05.2'N" })}</div>
            <div style={{ flex: 1 }}>{F('経度', 'longitude', { ph: "133°00.5'E" })}</div>
          </div>
          {F('避難理由', 'evacReason', { textarea: true, ph: '例: 低気圧の接近に伴う強風・高波のため' })}
          <div style={{ fontSize: 13, fontWeight: 800, color: '#0f172a', marginTop: 16 }}>気象・海象</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>{F('風向', 'windDir', { ph: 'NW' })}</div>
            <div style={{ flex: 1 }}>{F('風速（m）', 'windSpeed')}</div>
            <div style={{ flex: 1 }}>{F('天候', 'weatherCond', { ph: '雨' })}</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>{F('視界（m）', 'visibility')}</div>
            <div style={{ flex: 1 }}>{F('底質', 'seabed', { ph: '泥' })}</div>
            <div style={{ flex: 1 }}>{F('水深（m）', 'depth')}</div>
            <div style={{ flex: 1 }}>{F('波高（m）', 'waveHeight')}</div>
          </div>
          {F('備考（気象・海象）', 'wxRemarks')}
          <div style={{ fontSize: 13, fontWeight: 800, color: '#0f172a', marginTop: 16 }}>投錨・抜錨</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>{F('投錨 予定', 'anchorPlan', { ph: '14 時 30 分' })}</div>
            <div style={{ flex: 1 }}>{F('投錨 実時間', 'anchorActual', { ph: '14 時 45 分' })}</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>{F('抜錨 予定', 'weighPlan')}</div>
            <div style={{ flex: 1 }}>{F('抜錨 実時間', 'weighActual')}</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>{F('使用錨鎖（節）', 'chainShackles', { ph: '5' })}</div>
            <div style={{ flex: 1 }}>
              <label style={lb}>機関S/B</label>
              <select style={inp} value={data.engineStandby ?? ''} onChange={e => set('engineStandby', e.target.value)}><option value="">－</option><option>有</option><option>無</option></select>
            </div>
          </div>
          <div style={{ fontSize: 13, fontWeight: 800, color: '#0f172a', marginTop: 16 }}>略図</div>
          <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.6 }}>紙に描いた略図や海図を撮影して貼れます。写真が無ければ文章で書いてください。</div>
          {data.sketchImage
            ? <div style={{ marginTop: 8, position: 'relative' }}>
                <img src={data.sketchImage} alt="略図" style={{ width: '100%', borderRadius: 10, border: '1px solid #e2e8f0' }} />
                <button onClick={() => set('sketchImage', '')} style={{ position: 'absolute', top: 8, right: 8, fontSize: 12, color: '#ef4444', background: '#fff', border: '1px solid #fecaca', borderRadius: 8, padding: '5px 10px', cursor: 'pointer' }}>削除</button>
              </div>
            : <label style={{ display: 'block', marginTop: 8, padding: 14, border: '1px dashed #94a3b8', borderRadius: 10, textAlign: 'center', fontSize: 14, fontWeight: 700, color: '#475569', cursor: 'pointer', background: '#fff' }}>
                📷 略図を撮影・選択
                <input type="file" accept="image/*" capture="environment" style={{ display: 'none' }}
                  onChange={async e => { const f = e.target.files?.[0]; e.target.value = ''; if (!f) return; try { set('sketchImage', await fileToDataUrl(f)); } catch { showToast('画像を読み込めませんでした', 'err'); } }} />
              </label>}
          {!data.sketchImage && F('略図（文章で）', 'sketchText', { textarea: true })}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 18, marginBottom: 4 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>停泊当直時間（{watchRows.length}行）</div>
            {watchRows.length < 8 && <button onClick={() => setWatchRows(rs => [...rs, emptyWatch()])} style={{ fontSize: 13, fontWeight: 800, color: '#2563eb', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '6px 12px', cursor: 'pointer' }}>＋ 追加</button>}
          </div>
          {watchRows.map((r, i) => (
            <div key={i} style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: 12, marginBottom: 10, background: '#fff' }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                <div style={{ flex: 1 }}><label style={lb}>時間帯（から）</label><input style={inp} value={r.from} placeholder="18:00" onChange={e => setWatchRow(i, { from: e.target.value })} /></div>
                <div style={{ paddingBottom: 14, color: '#64748b' }}>～</div>
                <div style={{ flex: 1 }}><label style={lb}>（まで）</label><input style={inp} value={r.to} placeholder="22:00" onChange={e => setWatchRow(i, { to: e.target.value })} /></div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ flex: 1 }}><label style={lb}>甲板部</label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input style={{ ...inp, flex: 1, minWidth: 0 }} value={r.deck} onChange={e => setWatchRow(i, { deck: e.target.value })} />
                    <button type="button" onClick={() => setPickerFor({ kind: 'watch', index: i, col: 'deck' })} style={{ width: 44, flexShrink: 0, borderRadius: 10, border: '1px solid #bae6fd', background: '#f0f9ff', color: '#0369a1', fontSize: 17, cursor: 'pointer' }}>👤</button>
                  </div></div>
                <div style={{ flex: 1 }}><label style={lb}>機関部</label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input style={{ ...inp, flex: 1, minWidth: 0 }} value={r.engine} onChange={e => setWatchRow(i, { engine: e.target.value })} />
                    <button type="button" onClick={() => setPickerFor({ kind: 'watch', index: i, col: 'engine' })} style={{ width: 44, flexShrink: 0, borderRadius: 10, border: '1px solid #bae6fd', background: '#f0f9ff', color: '#0369a1', fontSize: 17, cursor: 'pointer' }}>👤</button>
                  </div></div>
              </div>
            </div>
          ))}
          <div style={{ fontSize: 13, fontWeight: 800, color: '#0f172a', marginTop: 16 }}>報告書作成者　<span style={{ fontWeight: 400, color: '#64748b', fontSize: 12 }}>（あなたの名前と役職が入っています）</span></div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>{F('役職', 'reporterTitle', { ph: '船長' })}</div>
            <div style={{ flex: 1 }}>{NameField('氏名', 'reporterName', 'reporterTitle')}</div>
          </div>
          <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.6, marginTop: 10 }}>※ この記録は永久的に記録する事／全船及び事務所に必ず報告する事（「陸上へ提出」で事務所に届きます）</div>
        </>)}

        {/* ── 顛末書（社内様式・2026.10.30 原紙） ── */}
        {form === 'tenmatsu' && (<>
          {F('提出年月日', 'reportDate', { ph: '令和 8 年 11 月 2 日' })}
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>{F('宛先（会社名）', 'companyName')}</div>
            <div style={{ flex: 1 }}>{F('代表取締役', 'companyRep')}</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>{F('船名', 'shipName')}</div>
            <div style={{ flex: 1 }}>{F('港名', 'portName')}</div>
          </div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 10 }}>船長名・機関長名は選んだ船の乗組員から自動で入ります（👤で選び直せます）</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>{NameField('船長名', 'masterName')}</div>
            <div style={{ flex: 1 }}>{NameField('機関長名', 'chiefEngName')}</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>{F('当事者 役職', 'partyTitle', { ph: '一等航海士' })}</div>
            <div style={{ flex: 1 }}>{NameField('当事者 氏名', 'partyName', 'partyTitle')}</div>
          </div>
          {NameChips(c => setData(d => ({ ...d, partyName: c.user_name, partyTitle: c.position || c.license_grade || d.partyTitle || '' })))}
          {F('事例', 'caseTitle', { ph: '例: 係留索の切断' })}
          <div style={{ fontSize: 13, fontWeight: 800, color: '#0f172a', marginTop: 16 }}>気象・海象</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>{F('風向', 'windDir', { ph: 'NW' })}</div>
            <div style={{ flex: 1 }}>{F('風速', 'windSpeed', { ph: '15m' })}</div>
            <div style={{ flex: 1 }}>{F('天候', 'weatherCond', { ph: '雨' })}</div>
            <div style={{ flex: 1 }}>{F('視界', 'visibility')}</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>{F('底質', 'seabed')}</div>
            <div style={{ flex: 1 }}>{F('水深', 'depth')}</div>
            <div style={{ flex: 1 }}>{F('波高', 'waveHeight')}</div>
            <div style={{ flex: 1 }}>{F('備考', 'wxRemarks')}</div>
          </div>
          <div style={{ fontSize: 13, fontWeight: 800, color: '#0f172a', marginTop: 16 }}>発生 日時・場所</div>
          {F('発生日時', 'occurredAt', { ph: '11 月 1 日 14 時 30 分' })}
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>{F('場所', 'occurredPlace', { ph: '例: 船尾甲板' })}</div>
            <div style={{ flex: 1 }}>{F('港名（荷役地名等）', 'occurredPort')}</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}><label style={lb}>船内・船外</label>
              <select style={inp} value={data.inOutBoard ?? ''} onChange={e => set('inOutBoard', e.target.value)}><option value="">－</option><option>船内</option><option>船外</option></select></div>
            <div style={{ flex: 1 }}><label style={lb}>荷役中・荷役外</label>
              <select style={inp} value={data.cargoWork ?? ''} onChange={e => set('cargoWork', e.target.value)}><option value="">－</option><option>荷役中</option><option>荷役外</option></select></div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ width: 120 }}><label style={lb}>積荷</label>
              <select style={inp} value={data.cargoLoaded ?? ''} onChange={e => set('cargoLoaded', e.target.value)}><option value="">－</option><option>有</option><option>無</option></select></div>
            <div style={{ flex: 1 }}>{F('製品名', 'productName')}</div>
          </div>
          {F('発生内容', 'incidentDetail', { textarea: true })}
          {F('発生原因', 'incidentCause', { textarea: true })}
          {F('対応', 'incidentResponse', { textarea: true })}
          {F('今後の対策', 'countermeasure', { textarea: true })}
          <div style={{ fontSize: 13, fontWeight: 800, color: '#0f172a', marginTop: 16 }}>現場写真（{photos.length}/6）</div>
          <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.6 }}>寄り・引きの写真を添えてください。PDFの2ページ目に並びます。</div>
          {photos.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 8 }}>
              {photos.map((p, i) => (
                <div key={i} style={{ position: 'relative' }}>
                  <img src={p} alt={`写真${i + 1}`} style={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover', borderRadius: 10, border: '1px solid #e2e8f0' }} />
                  <button onClick={() => setPhotos(ps => ps.filter((_, idx) => idx !== i))} style={{ position: 'absolute', top: 6, right: 6, fontSize: 11, color: '#ef4444', background: '#fff', border: '1px solid #fecaca', borderRadius: 8, padding: '3px 8px', cursor: 'pointer' }}>削除</button>
                </div>
              ))}
            </div>
          )}
          {photos.length < 6 && (
            <label style={{ display: 'block', marginTop: 8, padding: 14, border: '1px dashed #94a3b8', borderRadius: 10, textAlign: 'center', fontSize: 14, fontWeight: 700, color: '#475569', cursor: 'pointer', background: '#fff' }}>
              📷 写真を撮影・選択
              <input type="file" accept="image/*" capture="environment" style={{ display: 'none' }}
                onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; addPhoto(f); }} />
            </label>
          )}
        </>)}

        {/* ── 船員旅費精算書（社内様式） ── */}
        {form === 'travelexp' && (<>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>{F('船名', 'shipName')}</div>
            <div style={{ flex: 1 }}>{NameField('船長氏名', 'masterName')}</div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 18, marginBottom: 4 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>旅費の明細（{travelRows.length}件）</div>
            <button onClick={() => setTravelRows(rs => [...rs, emptyTravel()])} style={{ fontSize: 13, fontWeight: 800, color: '#2563eb', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '6px 12px', cursor: 'pointer' }}>＋ 追加</button>
          </div>
          {travelRows.map((r, i) => (
            <div key={i} style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: 12, marginBottom: 10, background: '#fff' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 800, color: '#64748b' }}>#{i + 1}</span>
                {travelRows.length > 1 && <button onClick={() => setTravelRows(rs => rs.filter((_, idx) => idx !== i))} style={{ fontSize: 12, color: '#ef4444', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '5px 10px', cursor: 'pointer' }}>削除</button>}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ width: 80 }}><label style={lb}>月</label><input style={inp} inputMode="numeric" value={r.month} onChange={e => setTravelRow(i, { month: e.target.value })} /></div>
                <div style={{ width: 80 }}><label style={lb}>日</label><input style={inp} inputMode="numeric" value={r.day} onChange={e => setTravelRow(i, { day: e.target.value })} /></div>
                <div style={{ flex: 1 }}><label style={lb}>金額（円）</label><input style={inp} inputMode="numeric" value={r.amount} onChange={e => setTravelRow(i, { amount: e.target.value })} /></div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <label style={lb}>発駅</label>
                  <Combo value={r.fromStation} onChange={v => setTravelRow(i, { fromStation: v })} options={opts.station} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={lb}>着駅</label>
                  <Combo value={r.toStation} onChange={v => setTravelRow(i, { toStation: v })} options={opts.station} />
                </div>
              </div>
              <label style={lb}>交通内容（電車・タクシーなど）</label>
              <Combo value={r.transport} onChange={v => setTravelRow(i, { transport: v })} options={opts.transport} />
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', borderRadius: 12, background: '#0f172a', color: '#fff', marginBottom: 4 }}>
            <span style={{ fontSize: 13, fontWeight: 700 }}>合計</span>
            <span style={{ fontSize: 18, fontWeight: 800 }}>{travelTotal.toLocaleString('ja-JP')} 円</span>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 18 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>受領の報告</div>
            <button onClick={() => setPickerFor({ kind: 'person', index: 0 })} style={{ fontSize: 12, fontWeight: 800, color: '#0369a1', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: '5px 10px', cursor: 'pointer' }}>👤 船員から選択</button>
          </div>
          {F('報告年月日', 'receiptDate', { ph: '令和 7 年 9 月 13 日' })}
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <label style={lb}>職名</label>
              <Combo value={data.reportJobTitle ?? ''} onChange={v => set('reportJobTitle', v)} options={opts.jobTitle} ph="一等航海士" />
            </div>
            <div style={{ flex: 1 }}>{F('氏名', 'reportPerson')}</div>
          </div>
          <div style={{ fontSize: 11.5, color: '#94a3b8', lineHeight: 1.8, marginTop: 8 }}>
            ※本人捺印及び船長もしくは担当者の確認印がないものは無効とする<br />
            ※旅費以外の精算書は記入しないようお願いいたします
          </div>
        </>)}

        {/* ── 労務管理記録簿（勤怠から自動作成） ── */}
        {form === 'roumu' && (<>
          <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 12, padding: '10px 12px', fontSize: 12.5, color: '#0c4a6e', lineHeight: 1.8, marginBottom: 6 }}>
            国土交通省の様式と同じ記録簿を、打刻された勤怠から作ります。<br />
            作業の開始・終了、休息時間、休日・有給は自動で入ります。
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>対象の船員</div>
            <button onClick={() => setPickerFor({ kind: 'target', index: 0 })} style={{ fontSize: 12, fontWeight: 800, color: '#0369a1', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: '5px 10px', cursor: 'pointer' }}>👤 船員から選択</button>
          </div>
          <input style={{ ...inp, background: '#f8fafc', marginTop: 6 }} readOnly value={data.userName ?? ''} placeholder="未選択" />
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <label style={lb}>年</label>
              <select style={inp} value={data.year ?? ''} onChange={e => set('year', e.target.value)}>
                {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={lb}>月</label>
              <select style={inp} value={data.month ?? ''} onChange={e => set('month', e.target.value)}>
                {Array.from({ length: 12 }, (_, i) => i + 1).map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          </div>

          <div style={{ fontSize: 14, fontWeight: 800, color: '#0f172a', marginTop: 18 }}>様式の設定</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}><label style={lb}>時間外労働協定</label>
              <select style={inp} value={data.overtimeAgreement ?? ''} onChange={e => set('overtimeAgreement', e.target.value)}><option value="">－</option><option>有</option><option>無</option></select>
            </div>
            <div style={{ flex: 1 }}><label style={lb}>補償休日労働協定</label>
              <select style={inp} value={data.compAgreement ?? ''} onChange={e => set('compAgreement', e.target.value)}><option value="">－</option><option>有</option><option>無</option></select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}><label style={lb}>休息時間分割協定</label>
              <select style={inp} value={data.restSplitAgreement ?? ''} onChange={e => set('restSplitAgreement', e.target.value)}><option value="">－</option><option>有</option><option>無</option></select>
            </div>
            <div style={{ flex: 1 }}>{F('基準労働期間（月）', 'basePeriodMonths', { ph: '1' })}</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}><label style={lb}>休日日数の定め</label>
              <select style={inp} value={data.holidayDefined ?? '有'} onChange={e => set('holidayDefined', e.target.value)}><option>有</option><option>無</option></select>
            </div>
            <div style={{ flex: 1 }}>{F('休日日数', 'holidayDays', { ph: '4' })}</div>
            <div style={{ flex: 1 }}>{F('有給の必要日数', 'paidLeaveDays', { ph: '10' })}</div>
          </div>
          {F('労務管理責任者', 'laborManager')}
          {F('船舶所有者', 'shipowner')}
        </>)}

        <button onClick={openPreview} disabled={busy}
          style={{ width: '100%', marginTop: 18, padding: 13, borderRadius: 12, border: '1px dashed #94a3b8', background: '#fff', color: '#475569', fontSize: 14, fontWeight: 800, cursor: 'pointer' }}>
          🔍 プレビューを表示
        </button>

        {/* アクション（下にスクロールして操作。PDF・印刷・陸上へ提出の3つ） */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 24 }}>
          <button disabled={busy} onClick={() => openCert(true)} style={{ width: '100%', padding: 15, borderRadius: 12, border: 'none', color: '#fff', fontSize: 15, fontWeight: 800, cursor: busy ? 'wait' : 'pointer', background: 'linear-gradient(135deg,#0ea5e9,#0284c7)' }}>📄 PDFをダウンロード</button>
          <button disabled={busy} onClick={() => openCert(false)} style={{ width: '100%', padding: 15, borderRadius: 12, border: 'none', color: '#fff', fontSize: 15, fontWeight: 800, cursor: busy ? 'wait' : 'pointer', background: 'linear-gradient(135deg,#334155,#0f172a)' }}>🖨 印刷する</button>
          <button disabled={busy} onClick={submitToOffice} style={{ width: '100%', padding: 15, borderRadius: 12, border: 'none', color: '#fff', fontSize: 15, fontWeight: 800, cursor: busy ? 'wait' : 'pointer', background: 'linear-gradient(135deg,#4f46e5,#4338ca)' }}>🏢 陸上へ提出する</button>
        </div>
      </div>
    </div>

      {/* 船員選択シート */}
      {pickerFor && (
        <div onClick={() => setPickerFor(null)} style={{ position: 'fixed', inset: 0, zIndex: 9000, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 480, maxHeight: '80vh', background: '#fff', borderRadius: '20px 20px 0 0', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '16px 18px 8px', fontSize: 15, fontWeight: 800, color: '#0f172a' }}>船員を選択</div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '0 10px 12px' }}>
              {crewForVessel.length === 0 && <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>乗組員が見つかりません</div>}
              {crewForVessel.map(c => (
                <button key={c.user_id} onClick={() => applyCrewToRow(c)} style={{ width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 10, padding: '12px', border: 'none', borderBottom: '1px solid #f1f5f9', background: '#fff', cursor: 'pointer' }}>
                  <span style={{ width: 34, height: 34, borderRadius: '50%', background: 'linear-gradient(135deg,#0ea5e9,#6366f1)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, flexShrink: 0 }}>{c.user_name.slice(0, 1)}</span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{c.user_name}</span>
                    <span style={{ display: 'block', fontSize: 11, color: '#94a3b8' }}>{[c.position || c.license_grade, c.vessel_name].filter(Boolean).join('・')}</span>
                  </span>
                  {/* 選択中の船との関わり（乗船中・過去乗船）を示すバッジ。優先順はサーバー側で並び済み */}
                  {selectedVesselId && c.involvement === 'on_board' && (
                    <span style={{ flexShrink: 0, fontSize: 10.5, fontWeight: 800, color: '#0057d9', background: '#dbeafe', borderRadius: 999, padding: '3px 9px' }}>乗船中</span>
                  )}
                  {selectedVesselId && c.involvement === 'past' && (
                    <span style={{ flexShrink: 0, fontSize: 10.5, fontWeight: 800, color: '#64748b', background: '#f1f5f9', borderRadius: 999, padding: '3px 9px' }}>過去乗船</span>
                  )}
                </button>
              ))}
            </div>
            <button onClick={() => setPickerFor(null)} style={{ padding: 14, border: 'none', borderTop: '1px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>閉じる</button>
          </div>
        </div>
      )}

      {/* プレビューモーダル */}
      {preview && (
        <div onClick={() => { if (preview.url) URL.revokeObjectURL(preview.url); setPreview(null); }}
          style={{ position: 'fixed', inset: 0, zIndex: 9500, background: 'rgba(15,23,42,0.85)', display: 'flex', flexDirection: 'column' }}>
          <div className="app-header" style={{ padding: '0 14px 10px', display: 'flex', alignItems: 'center', gap: 8, color: '#fff', border: 'none', boxShadow: 'none' }}>
            <span style={{ fontSize: 14, fontWeight: 800, flex: 1 }}>プレビュー</span>
            {/* ズーム操作（アプリはピンチ無効のためボタンで拡大縮小） */}
            <button onClick={() => zoomTo(zoom - 0.5)} style={{ width: 34, height: 34, borderRadius: 9, border: 'none', background: 'rgba(255,255,255,0.18)', color: '#fff', fontSize: 20, fontWeight: 700, cursor: 'pointer' }}>－</button>
            <span style={{ minWidth: 48, textAlign: 'center', fontSize: 13, fontWeight: 700 }}>{Math.round(zoom * 100)}%</span>
            <button onClick={() => zoomTo(zoom + 0.5)} style={{ width: 34, height: 34, borderRadius: 9, border: 'none', background: 'rgba(255,255,255,0.18)', color: '#fff', fontSize: 20, fontWeight: 700, cursor: 'pointer' }}>＋</button>
            <button onClick={() => { focalRef.current = null; setZoom(1); previewScrollRef.current?.scrollTo({ top: 0, left: 0 }); }} style={{ padding: '0 12px', height: 34, borderRadius: 9, border: 'none', background: 'rgba(255,255,255,0.18)', color: '#fff', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>全体</button>
            <button onClick={() => { if (preview.url) URL.revokeObjectURL(preview.url); setPreview(null); }} style={{ background: 'rgba(255,255,255,0.18)', border: 'none', color: '#fff', width: 34, height: 34, borderRadius: 9, fontSize: 16, cursor: 'pointer' }}>✕</button>
          </div>
          <div ref={previewScrollRef} onClick={e => e.stopPropagation()} style={{ flex: 1, overflow: 'auto', padding: 0, WebkitOverflowScrolling: 'touch', touchAction: 'pan-x pan-y' }}>
            {preview.loading
              ? <div style={{ color: '#e2e8f0', padding: 60, fontSize: 14, textAlign: 'center' }}>生成中…</div>
              : <img ref={imgRef} alt="プレビュー" src={preview.url} draggable={false} style={{ width: `${zoom * 100}%`, maxWidth: 'none', display: 'block', background: '#fff', boxShadow: '0 2px 16px rgba(0,0,0,0.4)' }} />}
          </div>
          <div style={{ padding: '6px 12px calc(6px + env(safe-area-inset-bottom))', textAlign: 'center', color: '#94a3b8', fontSize: 11 }}>2本指のピンチ、または上部の ＋／－ で拡大・縮小できます</div>
        </div>
      )}

      {/* iPhone: PDFの印刷・共有シート（共有シートの「プリント」でAirPrint、「ファイルに保存」で保存） */}
      {pdfReady && (
        <div onClick={() => setPdfReady(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1000 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '20px 20px 0 0', padding: '20px 20px calc(20px + env(safe-area-inset-bottom))', width: '100%', maxWidth: 480, boxShadow: '0 -8px 32px rgba(0,0,0,0.15)' }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', marginBottom: 4 }}>📄 PDFができました</div>
            <div style={{ fontSize: 12.5, color: '#64748b', lineHeight: 1.7, marginBottom: 14 }}>
              {pdfReady.title}<br />
              「印刷・共有」を押し、出てきたメニューの<strong>「プリント」</strong>で印刷、<strong>「ファイルに保存」</strong>で保存できます。
            </div>
            {pdfReady.file && typeof navigator.canShare === 'function' && navigator.canShare({ files: [pdfReady.file] }) && (
              <button
                onClick={() => { navigator.share({ files: [pdfReady.file!], title: pdfReady.title }).catch(() => { /* 閉じただけ */ }); }}
                style={{ width: '100%', padding: 15, marginBottom: 10, borderRadius: 12, border: 'none', color: '#fff', fontSize: 15, fontWeight: 800, cursor: 'pointer', background: 'linear-gradient(135deg,#334155,#0f172a)' }}>
                🖨 印刷・共有する
              </button>
            )}
            <button
              onClick={() => { window.open(pdfReady.url, '_blank'); }}
              style={{ width: '100%', padding: 14, marginBottom: 10, borderRadius: 12, border: '1.5px solid #bfdbfe', background: '#eff6ff', color: '#1d4ed8', fontSize: 14, fontWeight: 800, cursor: 'pointer' }}>
              📄 PDFを開く（画面右上・下の共有ボタンから印刷）
            </button>
            <button onClick={() => setPdfReady(null)} style={{ width: '100%', padding: 12, border: 'none', borderRadius: 12, background: 'transparent', color: '#64748b', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>閉じる</button>
          </div>
        </div>
      )}

      {/* 送付先（陸上管理者）選択シート */}
      {recipOpen && (
        <div onClick={() => setRecipOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1000 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '20px 20px 0 0', padding: 20, width: '100%', maxWidth: 480, maxHeight: '80dvh', overflowY: 'auto', boxShadow: '0 -8px 32px rgba(0,0,0,0.15)' }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', marginBottom: 4 }}>送付先の陸上管理者</div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 12 }}>複数選択できます。未選択の場合は全員へ提出します。</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 14 }}>
              {shoreAdmins.length === 0 && <div style={{ fontSize: 13, color: '#94a3b8', padding: 6 }}>宛先候補が取得できませんでした（全員へ提出します）。</div>}
              {shoreAdmins.map(a => (
                <label key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 10px', borderRadius: 10, background: recipSel.includes(a.id) ? '#eef2ff' : '#f8fafc', cursor: 'pointer' }}>
                  <input type="checkbox" checked={recipSel.includes(a.id)} onChange={() => setRecipSel(s => s.includes(a.id) ? s.filter(x => x !== a.id) : [...s, a.id])} />
                  <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{a.user_name}</span>
                </label>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setRecipOpen(false)} style={{ flex: 1, padding: 14, border: '1px solid #e2e8f0', borderRadius: 12, background: '#fff', color: '#475569', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>キャンセル</button>
              <button onClick={doSubmitToOffice} disabled={busy} style={{ flex: 2, padding: 14, border: 'none', borderRadius: 12, background: '#4338ca', color: '#fff', fontWeight: 800, fontSize: 14, cursor: busy ? 'wait' : 'pointer' }}>
                {recipSel.length > 0 ? `${recipSel.length}名へ提出する` : '全員へ提出する'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* トースト */}
      {toast && (
        <div style={{ position: 'fixed', left: '50%', bottom: 92, transform: 'translateX(-50%)', zIndex: 9999, background: toast.type === 'err' ? '#dc2626' : '#0f172a', color: '#fff', padding: '11px 18px', borderRadius: 12, fontSize: 13.5, fontWeight: 700, maxWidth: '90vw', textAlign: 'center', boxShadow: '0 8px 24px rgba(0,0,0,0.3)' }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
};

export default CertCreatePage;
